import {
  Component,
  ViewChild,
  OnChanges,
  OnInit,
  Input,
  forwardRef,
} from '@angular/core'
import {
  FormGroup,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms'

import {Observable, BehaviorSubject} from 'rxjs'
import {
  distinctUntilChanged,
  finalize,
  switchMap,
  debounceTime,
  tap,
  map,
  filter,
} from 'rxjs/operators'

import {NgSelectComponent} from '@ng-select/ng-select'

import {TerminalService, TerminalType} from '@app/core/services/rest-api'
import {LoggerService} from '@app/core/services/logger.service'

@Component({
  selector: 'terminal-type-selector',
  templateUrl: './terminal-type-selector.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TerminalTypeSelectorComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class TerminalTypeSelectorComponent
  implements OnInit, ControlValueAccessor, OnChanges
{
  @ViewChild(NgSelectComponent, {static: true}) select: NgSelectComponent

  @Input() placeholder = ''
  @Input() formGroup: FormGroup
  @Input() domain: string
  @Input() codeOnly: boolean = true
  @Input() filter: (TerminalType) => boolean
  @Input() openSelect: boolean = true
  ngOnChanges(changes) {
    if (changes.openSelect && changes.openSelect.currentValue) {
      this.select.open()
    }
  }

  public terminalTypeList: Observable<TerminalType[]>
  public typeahead = new BehaviorSubject<string>(null)
  public listLoading = false
  public terminalType: TerminalType
  _onChanged: any
  _terminalTypes = new BehaviorSubject<TerminalType[]>(null)

  constructor(
    private terminalSvc: TerminalService,
    private logger: LoggerService,
  ) {}

  ngOnInit() {
    if (!this.domain) {
      this.domain = null
    }

    /* On typeahead debnounce and make unique */
    /* Then search in backend */
    this.terminalTypeList = this.typeahead.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => {
        this.listLoading = true
      }),
      switchMap((term) => this.searchTerminalTypes(term)),
      finalize(() => {
        this.listLoading = false
      }),
    )

    this.terminalSvc
      .getAvailableTerminalTypes(this.domain)
      .subscribe((data) => {
        if (data && this.filter) {
          data = data.filter(this.filter)
        }
        this._terminalTypes.next(data)
      })
  }

  searchTerminalTypes(term: string): Observable<TerminalType[]> {
    //We don't need to refresh this necessarily
    return this._terminalTypes.pipe(
      map((data) => {
        if (!data) {
          return []
        }
        return data
          .filter(
            (type) =>
              !term || type.name.toLowerCase().includes(term.toLowerCase()),
          )
          .sort((a, b) => {
            const prefixA = a.code.substr(0, 3)
            const prefixB = b.code.substr(0, 3)
            //reverse sort on type prefix -> P6X before P68
            if (prefixA > prefixB) return -1
            if (prefixB > prefixA) return 1
            if (a.code > b.code) return 1
            if (b.code > a.code) return -1
            return 0
          })
      }),
    )
  }

  onOpen() {
    /* Work around typeahead not being cleared on new open */
    this.typeahead.next(null)
  }

  /* On selecting a new domain from list fire the change listener */
  onChange(terminalType) {
    if (this._onChanged) {
      if (terminalType) {
        if (this.codeOnly) {
          this._onChanged(terminalType.code)
        } else {
          this._onChanged(terminalType)
        }
      } else {
        this._onChanged(null)
      }
    }
  }

  /* New value writtern from form. Lookup the domain for the code and set as value */
  private sub = null
  writeValue(code: any) {
    this.listLoading = true
    if (code !== null) {
      if (this.sub) {
        this.sub.unsubscribe()
      }
      this.sub = this._terminalTypes
        .pipe(filter((value) => value !== null))
        .subscribe((types) => {
          let type = types.find((type: TerminalType) => type.code === code)
          this.select.writeValue(type)
        })
    }
  }

  registerOnChange(fn: any) {
    this._onChanged = fn
  }

  registerOnTouched(fn: any) {
    this.select.registerOnTouched(fn)
  }
}
